import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { hrAPI } from '../lib/api';
import { useHrPermissions } from '../hooks/useHrPermissions';
import { formatDate } from '../lib/utils';
import {
  ArrowLeft, Loader2, Save, UserCircle, Briefcase, Shield, Trash2,
  CreditCard, AlertTriangle, Link as LinkIcon, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  HrDocumentsTab, HrLeaveTab, HrPerformanceTab, HrTrainingTab, HrDisciplinariesTab,
} from '../components/hr/HrRecordsTabs';
import { HrPayrollTab } from '../components/hr/HrPayrollTab';

const fmtHM = (seconds) => {
  if (!seconds || seconds < 0) return '0h 0m';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m.toString().padStart(2, '0')}m`;
};

const Section = ({ title, icon: Icon, children, testId }) => (
  <Card className="border-slate-200" data-testid={testId}>
    <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2">{Icon && <Icon className="w-4 h-4 text-slate-500" />}{title}</CardTitle></CardHeader>
    <CardContent className="space-y-3">{children}</CardContent>
  </Card>
);

const Field = ({ label, value, onChange, type = 'text', placeholder = '', readOnly = false, testId }) => (
  <div>
    <Label className="text-xs text-slate-600">{label}</Label>
    {readOnly ? (
      <p className="text-sm text-slate-900 mt-1">{value || <span className="text-slate-400">—</span>}</p>
    ) : (
      <Input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testId} />
    )}
  </div>
);

export const HrEmployeeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { perms } = useHrPermissions();
  const [emp, setEmp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [linkEngOpen, setLinkEngOpen] = useState(false);
  const [linkUserOpen, setLinkUserOpen] = useState(false);
  const [engLookup, setEngLookup] = useState({ engineers: [], engineer_accounts: [] });
  const [userLookup, setUserLookup] = useState([]);
  const [draftEng, setDraftEng] = useState({ engineer_id: '', engineer_account_id: '' });
  const [draftUser, setDraftUser] = useState('');
  const [timesheets, setTimesheets] = useState(null);

  const fetch = async () => {
    setLoading(true);
    try {
      const r = await hrAPI.getEmployee(id);
      setEmp(r.data);
      setDraftEng({ engineer_id: r.data.engineer_id || '', engineer_account_id: r.data.engineer_account_id || '' });
      setDraftUser(r.data.linked_user_id || '');
      if (r.data.engineer_account_id) {
        try {
          const ts = await hrAPI.employeeTimesheets(id, 30);
          setTimesheets(ts.data);
        } catch {}
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load employee');
      navigate('/hr');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); /* eslint-disable-next-line */ }, [id]);

  const update = (patch) => setEmp(prev => ({ ...prev, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await hrAPI.updateEmployee(id, emp);
      setEmp(r.data);
      toast.success('Saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
    } finally { setSaving(false); }
  };

  const openEngLink = async () => {
    if (engLookup.engineers.length === 0) {
      try {
        const r = await hrAPI.engineerLookup();
        setEngLookup(r.data);
      } catch {}
    }
    setLinkEngOpen(true);
  };

  const openUserLink = async () => {
    if (userLookup.length === 0) {
      try {
        const r = await hrAPI.userLookup();
        setUserLookup(r.data);
      } catch {}
    }
    setLinkUserOpen(true);
  };

  const doLinkEngineer = async () => {
    try {
      const r = await hrAPI.linkEngineer(id, draftEng);
      setEmp(r.data);
      toast.success('Engineer link updated');
      setLinkEngOpen(false);
      if (r.data.engineer_account_id) {
        const ts = await hrAPI.employeeTimesheets(id, 30);
        setTimesheets(ts.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Link failed');
    }
  };

  const doLinkUser = async () => {
    try {
      const r = await hrAPI.linkUser(id, draftUser);
      setEmp(r.data);
      toast.success(draftUser ? 'User linked' : 'User unlinked');
      setLinkUserOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Link failed');
    }
  };

  const doTerminate = async () => {
    if (!window.confirm(`Mark ${emp.first_name} ${emp.last_name} as terminated?`)) return;
    try {
      await hrAPI.deleteEmployee(id);
      toast.success('Employee terminated');
      navigate('/hr');
    } catch (e) {
      toast.error('Could not terminate');
    }
  };

  if (loading || !emp) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      </Layout>
    );
  }

  const isHrAdmin = perms?.is_hr_admin;
  const readOnly = !isHrAdmin;
  const sensitiveHidden = emp._sensitive_redacted;

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in max-w-5xl mx-auto" data-testid="hr-employee-detail-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex items-start gap-3">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/hr"><ArrowLeft className="w-4 h-4" /></Link>
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 font-['Manrope'] flex items-center gap-2">
                <UserCircle className="w-8 h-8 text-blue-600" />
                {emp.first_name} {emp.last_name}
              </h1>
              <p className="text-slate-500 text-sm mt-0.5">{emp.job_title || 'No job title'} · {emp.work_email || '—'}</p>
              <div className="flex gap-2 mt-2">
                <Badge className="bg-slate-100 text-slate-700 border border-slate-200 text-[10px]">
                  {(emp.employment_status || 'active').replace('_', ' ')}
                </Badge>
                {emp.engineer_id && (
                  <Badge className="bg-blue-100 text-blue-700 border border-blue-200 text-[10px]">
                    <Briefcase className="w-3 h-3 mr-1" /> Engineer linked
                  </Badge>
                )}
                {emp.linked_user_id && (
                  <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 text-[10px]">
                    Self-service enabled
                  </Badge>
                )}
              </div>
            </div>
          </div>
          {isHrAdmin && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={openUserLink} data-testid="link-user-btn"><LinkIcon className="w-4 h-4 mr-1" /> Link User</Button>
              <Button variant="outline" onClick={openEngLink} data-testid="link-engineer-btn"><Briefcase className="w-4 h-4 mr-1" /> Link Engineer</Button>
              {emp.employment_status !== 'terminated' && (
                <Button variant="ghost" className="text-rose-600 hover:text-rose-700" onClick={doTerminate} data-testid="terminate-btn"><Trash2 className="w-4 h-4 mr-1" /> Terminate</Button>
              )}
              <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                Save
              </Button>
            </div>
          )}
        </div>

        {sensitiveHidden && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-xs">
            <Shield className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>Salary, bank details, tax code, NI and UTR are visible to HR Admins only.</span>
          </div>
        )}

        <Tabs defaultValue="personal">
          <TabsList>
            <TabsTrigger value="personal" data-testid="tab-personal">Personal</TabsTrigger>
            <TabsTrigger value="employment" data-testid="tab-employment">Employment</TabsTrigger>
            <TabsTrigger value="payroll" data-testid="tab-payroll">Payroll {sensitiveHidden && <Shield className="w-3 h-3 ml-1 text-amber-500" />}</TabsTrigger>
            <TabsTrigger value="timesheets" data-testid="tab-timesheets">Timesheets</TabsTrigger>
            <TabsTrigger value="payslips" data-testid="tab-payslips">Payslips</TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
            <TabsTrigger value="leave" data-testid="tab-leave">Leave</TabsTrigger>
            <TabsTrigger value="performance" data-testid="tab-performance">Performance</TabsTrigger>
            <TabsTrigger value="training" data-testid="tab-training">Training</TabsTrigger>
            {isHrAdmin && <TabsTrigger value="disciplinaries" data-testid="tab-disciplinaries">Disciplinaries</TabsTrigger>}
          </TabsList>

          <TabsContent value="personal" className="space-y-4">
            <Section title="Identity" icon={UserCircle} testId="section-identity">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="First Name" value={emp.first_name} onChange={(v) => update({ first_name: v })} readOnly={readOnly} testId="f-first" />
                <Field label="Last Name" value={emp.last_name} onChange={(v) => update({ last_name: v })} readOnly={readOnly} testId="f-last" />
                <Field label="Date of Birth" type="date" value={emp.date_of_birth} onChange={(v) => update({ date_of_birth: v })} readOnly={readOnly} testId="f-dob" />
                <div>
                  <Label className="text-xs text-slate-600">Gender</Label>
                  {readOnly ? <p className="text-sm mt-1">{emp.gender || '—'}</p> : (
                    <Select value={emp.gender || ''} onValueChange={(v) => update({ gender: v })}>
                      <SelectTrigger data-testid="f-gender"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="non_binary">Non-binary</SelectItem>
                        <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <Field label="Personal Email" value={emp.personal_email} onChange={(v) => update({ personal_email: v })} type="email" readOnly={readOnly} testId="f-pemail" />
                <Field label="Personal Phone" value={emp.personal_phone} onChange={(v) => update({ personal_phone: v })} readOnly={readOnly} testId="f-pphone" />
              </div>
            </Section>

            <Section title="Address" testId="section-address">
              <Field label="Address Line 1" value={emp.address_line1} onChange={(v) => update({ address_line1: v })} readOnly={readOnly} testId="f-addr1" />
              <Field label="Address Line 2" value={emp.address_line2} onChange={(v) => update({ address_line2: v })} readOnly={readOnly} testId="f-addr2" />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="City" value={emp.address_city} onChange={(v) => update({ address_city: v })} readOnly={readOnly} testId="f-city" />
                <Field label="Postcode" value={emp.address_postcode} onChange={(v) => update({ address_postcode: v })} readOnly={readOnly} testId="f-postcode" />
                <Field label="Country" value={emp.address_country} onChange={(v) => update({ address_country: v })} readOnly={readOnly} placeholder="United Kingdom" testId="f-country" />
              </div>
            </Section>

            <Section title="Emergency Contact & Next of Kin" testId="section-emergency">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Emergency Contact Name" value={emp.emergency_contact_name} onChange={(v) => update({ emergency_contact_name: v })} readOnly={readOnly} testId="f-em-name" />
                <Field label="Emergency Phone" value={emp.emergency_contact_phone} onChange={(v) => update({ emergency_contact_phone: v })} readOnly={readOnly} testId="f-em-phone" />
                <Field label="Relationship" value={emp.emergency_contact_relationship} onChange={(v) => update({ emergency_contact_relationship: v })} readOnly={readOnly} placeholder="e.g. Spouse" testId="f-em-rel" />
                <Field label="Next of Kin Name" value={emp.next_of_kin_name} onChange={(v) => update({ next_of_kin_name: v })} readOnly={readOnly} testId="f-nok-name" />
                <Field label="Next of Kin Phone" value={emp.next_of_kin_phone} onChange={(v) => update({ next_of_kin_phone: v })} readOnly={readOnly} testId="f-nok-phone" />
                <Field label="Relationship" value={emp.next_of_kin_relationship} onChange={(v) => update({ next_of_kin_relationship: v })} readOnly={readOnly} testId="f-nok-rel" />
              </div>
            </Section>
          </TabsContent>

          <TabsContent value="employment" className="space-y-4">
            <Section title="Employment Details" icon={Briefcase}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Job Title" value={emp.job_title} onChange={(v) => update({ job_title: v })} readOnly={readOnly} testId="f-job" />
                <Field label="Work Email" value={emp.work_email} onChange={(v) => update({ work_email: v })} readOnly={readOnly} testId="f-wemail" />
                <Field label="Start Date" type="date" value={emp.start_date} onChange={(v) => update({ start_date: v })} readOnly={readOnly} testId="f-start" />
                <Field label="End Date" type="date" value={emp.end_date} onChange={(v) => update({ end_date: v })} readOnly={readOnly} testId="f-end" />
                <div>
                  <Label className="text-xs text-slate-600">Employment Type</Label>
                  {readOnly ? <p className="text-sm mt-1">{emp.employment_type || '—'}</p> : (
                    <Select value={emp.employment_type || ''} onValueChange={(v) => update({ employment_type: v })}>
                      <SelectTrigger data-testid="f-emp-type"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="full_time">Full-time</SelectItem>
                        <SelectItem value="part_time">Part-time</SelectItem>
                        <SelectItem value="contractor">Contractor</SelectItem>
                        <SelectItem value="apprentice">Apprentice</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Status</Label>
                  {readOnly ? <p className="text-sm mt-1">{emp.employment_status}</p> : (
                    <Select value={emp.employment_status || 'active'} onValueChange={(v) => update({ employment_status: v })}>
                      <SelectTrigger data-testid="f-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="on_leave">On Leave</SelectItem>
                        <SelectItem value="terminated">Terminated</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>
            </Section>
            <Section title="Documents">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Driving Licence Number" value={emp.driving_licence_number} onChange={(v) => update({ driving_licence_number: v })} readOnly={readOnly} testId="f-driving" />
                <Field label="Passport Number" value={emp.passport_number} onChange={(v) => update({ passport_number: v })} readOnly={readOnly} testId="f-passport" />
              </div>
              <Field label="Notes" value={emp.notes} onChange={(v) => update({ notes: v })} readOnly={readOnly} testId="f-notes" />
            </Section>
          </TabsContent>

          <TabsContent value="payroll" className="space-y-4">
            {sensitiveHidden ? (
              <Card className="border-amber-200 bg-amber-50/40">
                <CardContent className="p-8 text-center">
                  <Shield className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                  <p className="font-semibold text-amber-900">Payroll details are restricted to HR Admins.</p>
                  <p className="text-xs text-amber-700 mt-1">Speak to an HR Admin to view or update these fields.</p>
                </CardContent>
              </Card>
            ) : (
              <>
                <Section title="Tax & Identifiers" icon={Shield}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <Field label="National Insurance Number" value={emp.ni_number} onChange={(v) => update({ ni_number: v })} readOnly={readOnly} testId="f-ni" />
                    <Field label="UTR Number" value={emp.utr_number} onChange={(v) => update({ utr_number: v })} readOnly={readOnly} placeholder="For contractors" testId="f-utr" />
                    <Field label="Tax Code" value={emp.tax_code} onChange={(v) => update({ tax_code: v })} readOnly={readOnly} placeholder="e.g. 1257L" testId="f-tax" />
                  </div>
                </Section>
                <Section title="Salary" icon={CreditCard}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <Field label="Annual Salary (£)" type="number" value={emp.salary_annual} onChange={(v) => update({ salary_annual: parseFloat(v) || 0 })} readOnly={readOnly} testId="f-salary" />
                    <Field label="Currency" value={emp.salary_currency || 'GBP'} onChange={(v) => update({ salary_currency: v })} readOnly={readOnly} testId="f-currency" />
                  </div>
                </Section>
                <Section title="Bank Account (Payroll)" icon={CreditCard}>
                  <div className="flex items-start gap-2 p-2.5 rounded bg-amber-50 border border-amber-200 text-xs text-amber-900 mb-2">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>Bank details are HR Admin-only. They are encrypted at rest in the nightly backup.</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <Field label="Account Holder Name" value={emp.bank_account_name} onChange={(v) => update({ bank_account_name: v })} readOnly={readOnly} testId="f-bank-name" />
                    <Field label="Sort Code" value={emp.bank_sort_code} onChange={(v) => update({ bank_sort_code: v })} readOnly={readOnly} placeholder="00-00-00" testId="f-sort" />
                    <Field label="Account Number" value={emp.bank_account_number} onChange={(v) => update({ bank_account_number: v })} readOnly={readOnly} placeholder="8 digits" testId="f-acc-num" />
                  </div>
                </Section>
              </>
            )}
          </TabsContent>

          <TabsContent value="timesheets" className="space-y-3">
            {!emp.engineer_account_id ? (
              <Card className="border-slate-200">
                <CardContent className="p-8 text-center">
                  <Clock className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-slate-700">No engineer account linked</p>
                  <p className="text-xs text-slate-500 mt-1">Link this employee to an engineer login above to see their shifts.</p>
                  {isHrAdmin && <Button variant="outline" size="sm" className="mt-3" onClick={openEngLink}><Briefcase className="w-3 h-3 mr-1" /> Link Engineer</Button>}
                </CardContent>
              </Card>
            ) : !timesheets ? (
              <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
            ) : timesheets.shifts?.length === 0 ? (
              <Card className="border-slate-200"><CardContent className="p-8 text-center text-sm text-slate-400">No shifts in the last 30 days.</CardContent></Card>
            ) : (
              <Card className="border-slate-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span>Last 30 days</span>
                    <Badge className="bg-blue-50 text-blue-700 border-blue-200 border">{fmtHM(timesheets.total_seconds)}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1.5 max-h-96 overflow-y-auto">
                  {timesheets.shifts.map(s => (
                    <div key={s.id} className="flex items-center justify-between text-xs px-3 py-2 rounded border border-slate-100 bg-white" data-testid={`ts-${s.id}`}>
                      <div>
                        <p className="font-medium">{formatDate(s.work_date)}</p>
                        <p className="text-[10px] text-slate-500 font-mono">
                          {new Date(s.clock_in_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          {' → '}
                          {s.clock_out_at ? new Date(s.clock_out_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'open'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold tabular-nums">{fmtHM(s.duration_seconds)}</p>
                        <div className="flex gap-1 mt-0.5 justify-end">
                          {s.is_overtime && <Badge className="bg-amber-100 text-amber-700 border-amber-200 border text-[9px]">OT</Badge>}
                          {s.approval_status === 'pending' && <Badge className="bg-rose-100 text-rose-700 border-rose-200 border text-[9px]">pending</Badge>}
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="documents">
            <HrDocumentsTab employeeId={id} canWrite={isHrAdmin} isSelf={false} isHrAdmin={isHrAdmin} />
          </TabsContent>

          <TabsContent value="payslips">
            <HrPayrollTab employeeId={id} employee={emp} isHrAdmin={isHrAdmin} />
          </TabsContent>

          <TabsContent value="leave">
            <HrLeaveTab employeeId={id} canApprove={isHrAdmin} isSelf={false} isHrAdmin={isHrAdmin} />
          </TabsContent>

          <TabsContent value="performance">
            <HrPerformanceTab employeeId={id} canWrite={isHrAdmin || perms?.is_manager} isHrAdmin={isHrAdmin} />
          </TabsContent>

          <TabsContent value="training">
            <HrTrainingTab employeeId={id} isHrAdmin={isHrAdmin} />
          </TabsContent>

          {isHrAdmin && (
            <TabsContent value="disciplinaries">
              <HrDisciplinariesTab employeeId={id} />
            </TabsContent>
          )}
        </Tabs>

        {/* Link Engineer dialog */}
        <Dialog open={linkEngOpen} onOpenChange={setLinkEngOpen}>
          <DialogContent data-testid="link-engineer-dialog">
            <DialogHeader>
              <DialogTitle>Link to Engineer</DialogTitle>
              <DialogDescription>Choose the engineer record and login account. Time sheets sync from the engineer account.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Engineer Record (scheduling)</Label>
                <Select value={draftEng.engineer_id} onValueChange={(v) => setDraftEng({ ...draftEng, engineer_id: v })}>
                  <SelectTrigger data-testid="eng-select"><SelectValue placeholder="(none)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">(unlink)</SelectItem>
                    {engLookup.engineers.map(e => (<SelectItem key={e.id} value={e.id}>{e.name} {e.email ? `· ${e.email}` : ''}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Engineer Account (login + time clock)</Label>
                <Select value={draftEng.engineer_account_id} onValueChange={(v) => setDraftEng({ ...draftEng, engineer_account_id: v })}>
                  <SelectTrigger data-testid="eng-acc-select"><SelectValue placeholder="(none)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">(unlink)</SelectItem>
                    {engLookup.engineer_accounts.map(a => (<SelectItem key={a.id} value={a.id}>{a.name} {a.email ? `· ${a.email}` : ''}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setLinkEngOpen(false)}>Cancel</Button>
              <Button onClick={doLinkEngineer} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="eng-link-save">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Link User dialog */}
        <Dialog open={linkUserOpen} onOpenChange={setLinkUserOpen}>
          <DialogContent data-testid="link-user-dialog">
            <DialogHeader>
              <DialogTitle>Link to CRM User</DialogTitle>
              <DialogDescription>Linking a user enables self-service profile editing for that person.</DialogDescription>
            </DialogHeader>
            <Select value={draftUser} onValueChange={setDraftUser}>
              <SelectTrigger data-testid="user-select"><SelectValue placeholder="(none)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="">(unlink)</SelectItem>
                {userLookup.map(u => (<SelectItem key={u.id} value={u.id}>{u.name} · {u.email}</SelectItem>))}
              </SelectContent>
            </Select>
            <DialogFooter>
              <Button variant="outline" onClick={() => setLinkUserOpen(false)}>Cancel</Button>
              <Button onClick={doLinkUser} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="user-link-save">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default HrEmployeeDetail;
